import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';

const GIFTS = [
  { type: 'coeur_3', label: '❤️ Cœur', price: 3 },
  { type: 'rose_5', label: '🌹 Rose', price: 5 },
  { type: 'etoile_10', label: '⭐ Étoile', price: 10 },
  { type: 'couronne_20', label: '👑 Couronne', price: 20 },
  { type: 'diamant_50', label: '💎 Diamant', price: 50 },
  { type: 'super_diamant_100', label: '💎💎 Super Diamant', price: 100 }
];

const LivePage = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [liveData, setLiveData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gifts, setGifts] = useState([]);
  
  // Modal cadeau
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('ORANGE_MONEY');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendingGift, setSendingGift] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchLive();
    fetchGifts();
    
    // Rafraîchir toutes les 5 secondes
    const interval = setInterval(() => {
      fetchLive();
      fetchGifts();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const fetchLive = async () => {
    try {
      const res = await api.get('/live/current');
      setLiveData(res.data);
    } catch (error) {
      console.error('Error fetching live:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGifts = async () => {
    try {
      const res = await api.get('/live/gifts');
      setGifts(res.data.gifts || []);
    } catch (error) {
      console.error('Error fetching gifts:', error);
    }
  };

  const handleSendGift = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber) {
      toast.error('Entrez votre numéro');
      return;
    }

    setSendingGift(true);

    try {
      const response = await api.post('/live/send-gift', {
        gift_type: selectedGift.type,
        payment_method: paymentMethod,
        phone_number: phoneNumber
      });

      toast.success(`✅ ${selectedGift.label} envoyé ! ${response.data.payment_instructions}`);
      setShowGiftModal(false);
      setPhoneNumber('');
      
      // Rafraîchir les cadeaux après un délai
      setTimeout(fetchGifts, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi cadeau');
    } finally {
      setSendingGift(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 className="animate-spin text-[#E50914]" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pb-12">
      <Header />
      
      <main className="pt-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Outfit' }}>
          <span className="text-[#E50914]">AGRP BOX</span> TV
        </h1>
        <p className="text-zinc-400 mb-8">Diffusions en direct - 100% Gratuit</p>

        {!liveData?.live ? (
          <div className="text-center py-20">
            <div className="mb-6">
              <div className="w-24 h-24 mx-auto bg-zinc-900 rounded-full flex items-center justify-center">
                <span className="text-5xl">📺</span>
              </div>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Aucun live en cours</h2>
            <p className="text-zinc-400 mb-6">
              Restez connectés ! Un live peut démarrer à tout moment.
            </p>
            <div className="inline-block px-6 py-3 bg-zinc-900 rounded-lg text-zinc-300">
              ⚫ Hors ligne
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne principale - Vidéo */}
            <div className="lg:col-span-2 space-y-6">
              {/* Indicateur EN DIRECT */}
              <div className="bg-gradient-to-r from-red-900/30 to-red-600/30 border-2 border-red-500 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-2xl font-bold text-red-500">EN DIRECT</span>
                    </div>
                    <span className="text-white text-xl font-semibold">{liveData.video.title}</span>
                  </div>
                  <div className="text-zinc-400 text-sm">
                    {new Date(liveData.video.launched_at).toLocaleTimeString('fr-FR')}
                  </div>
                </div>
              </div>

              {/* Lecteur vidéo */}
              <div className="bg-black rounded-lg overflow-hidden shadow-2xl">
                <div className="relative" style={{ paddingBottom: '56.25%' }}>
                  <video
                    controls
                    autoPlay
                    className="absolute top-0 left-0 w-full h-full"
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${liveData.video.url}`}
                  >
                    Votre navigateur ne supporte pas la lecture vidéo.
                  </video>
                </div>
              </div>

              {/* Boutons cadeaux */}
              <div className="bg-zinc-900 rounded-lg p-6">
                <h3 className="text-white font-semibold mb-4 flex items-center">
                  <Gift className="mr-2 text-[#E50914]" />
                  🎁 Envoyer un Cadeau
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {GIFTS.map((gift) => (
                    <Button
                      key={gift.type}
                      onClick={() => {
                        setSelectedGift(gift);
                        setShowGiftModal(true);
                      }}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 flex flex-col items-center"
                    >
                      <span className="text-2xl mb-1">{gift.label.split(' ')[0]}</span>
                      <span className="text-sm">{gift.price}$</span>
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            {/* Colonne droite - Feed cadeaux */}
            <div className="lg:col-span-1">
              <div className="bg-zinc-900 rounded-lg p-6 sticky top-24">
                <h3 className="text-white font-semibold mb-4">🎁 Cadeaux récents ({gifts.length})</h3>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {gifts.length === 0 ? (
                    <p className="text-zinc-500 text-sm text-center py-8">
                      Aucun cadeau pour le moment. Soyez le premier !
                    </p>
                  ) : (
                    gifts.map((gift, index) => (
                      <div
                        key={index}
                        className="bg-zinc-800 rounded-lg p-3 border border-zinc-700 animate-fade-in"
                      >
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">
                            {gift.gift_type === 'coeur_3' && '❤️'}
                            {gift.gift_type === 'rose_5' && '🌹'}
                            {gift.gift_type === 'etoile_10' && '⭐'}
                            {gift.gift_type === 'couronne_20' && '👑'}
                            {gift.gift_type === 'diamant_50' && '💎'}
                            {gift.gift_type === 'super_diamant_100' && '💎💎'}
                          </span>
                          <div className="flex-1">
                            <p className="text-white font-semibold text-sm">{gift.sender_name}</p>
                            <p className="text-zinc-400 text-xs">a envoyé {gift.amount_usd}$</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modal Envoi Cadeau */}
      <Dialog open={showGiftModal} onOpenChange={setShowGiftModal}>
        <DialogContent className="glass border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-center">
              🎁 Envoyer {selectedGift?.label}
            </DialogTitle>
          </DialogHeader>
          
          {selectedGift && (
            <form onSubmit={handleSendGift} className="space-y-4 py-4">
              <div className="bg-zinc-900 rounded-lg p-6 text-center">
                <p className="text-4xl mb-2">{selectedGift.label.split(' ')[0]}</p>
                <p className="text-2xl font-bold text-[#F97316]">{selectedGift.price}$</p>
                <p className="text-xs text-zinc-400 mt-2">L'argent va à AGRP BOX</p>
              </div>

              <div>
                <Label>Méthode de paiement</Label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                    <SelectItem value="M_PESA">M-Pesa (Vodacom)</SelectItem>
                    <SelectItem value="AIRTEL_MONEY">Airtel Money</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Votre numéro</Label>
                <Input
                  type="tel"
                  placeholder="+243..."
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  required
                  className="bg-zinc-900 border-zinc-800 text-white"
                />
              </div>

              <Button
                type="submit"
                disabled={sendingGift}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 py-6 text-lg font-semibold"
              >
                {sendingGift ? <Loader2 className="animate-spin" /> : `ENVOYER CADEAU ${selectedGift.price}$`}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LivePage;
